import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Config from '@skin_config';
import { useTranslation } from '@common/hooks/translation';
import Timer from '@common/components/timer';
import { PopupContext } from '@app/dev/contexts/popup';
import { formatMoneyAmount } from '@common/helpers/amount';
import { GetCurrencyConfig } from '@common/helpers/selectors';
import LoaderButton from '@common/components/loaderButton';
import { useBalance } from '@app/dev/terminal/hooks/balance';

import CashoutAmountForm from '../amountForm';

import './style.scss';

const CashoutBase = ({
  title = 'Cashout',
  description,
  icon,
  img,
  loading,
  onSubmit,
  className = '',
  timerVariant,
  confirmButtonClassName = '',
  confirmButtonText = '',
  hasBack,
  onBack,
  hasPartialCashout = Config.cashout.enablePartial,
  placeholder,
}) => {
  const t = useTranslation();
  const [{ closePopup }] = useContext(PopupContext);
  const [isMax, setIsMax] = useState(false);
  const [amountErrorMessage, setAmountErrorMessage] = useState('');
  const { isAdminLoggedIn } = useSelector((state) => state.terminal);
  const currencyConfig = useSelector((state) => (
    GetCurrencyConfig(state, Config.main.currencySymbolRounding)
  ));

  const balance = useBalance();
  const [amount, setAmount] = useState(!hasPartialCashout ? balance : '');

  const needAdminForCashout = Config.main.needAdminConfirmation && !isAdminLoggedIn;

  const changeAmount = (value, _isMax) => {
    setAmount(value);
    setIsMax(_isMax);

    if (Number(value) > balance) setAmountErrorMessage('You don\'t have enough funds');
    else if (amountErrorMessage) setAmountErrorMessage('');
  };

  const onCashout = async () => {
    if (loading) return;

    if (hasPartialCashout && Number.isNaN(Number(amount))) return;

    if (hasPartialCashout && Number(amount) > balance) {
      setAmountErrorMessage('You don\'t have enough funds');
      return;
    }

    const resp = await onSubmit?.(Number(amount), isMax || !hasPartialCashout);

    if (resp?.errorMessage) setAmountErrorMessage(resp.errorMessage);
  };

  return (
    <div className={`popup-content popup-small cashout-main-popup ${className}`}>
      <i onClick={closePopup} className="global-icon icon-Close close-popup" />
      <h3 className="popup-title">{t(title)}</h3>
      <div className="popup-main-content">
        <p className="text-center">
          {t.jsx('Your current balance is {1}', <span className="color-hero">{formatMoneyAmount(balance, Config, currencyConfig)}</span>)}
        </p>

        {description}

        {img && <img src={img} className="cashout-icon-img" alt="cashout img" />}
        {icon && <i className={`global-icon ${icon} cashout-icon`} />}

        <Timer onFinish={closePopup} duration={Config.main.cashoutPopupTimer} variant={timerVariant ?? ''} />

        {needAdminForCashout && <p className="admin-confirm-text">{t('Please ask administrator for confirmation')}</p>}
      </div>
      {hasPartialCashout
        ? (
          <>
            <CashoutAmountForm
              amount={amount}
              onChange={changeAmount}
              errorMessage={amountErrorMessage}
              placeholder={placeholder}
              isMax={isMax}
            />

            <div className="popup-actions">
              {hasBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="popup-cancel-button"
                >
                  {t('Back')}
                </button>
              )}

              <LoaderButton
                type="button"
                onClick={onCashout}
                className={`popup-confirm-button ${confirmButtonClassName}`}
                loading={loading}
                disabled={!+amount || needAdminForCashout}
              >
                {t(confirmButtonText || 'Cash out')}
              </LoaderButton>
            </div>
          </>
        ) : (
          <div className="popup-actions">
            {hasBack && (
              <button
                type="button"
                onClick={onBack}
                className="popup-cancel-button"
              >
                {t('Back')}
              </button>
            )}

            <LoaderButton
              type="button"
              onClick={onCashout}
              disabled={needAdminForCashout}
              className={`popup-confirm-button ${confirmButtonClassName}`}
              loading={loading}
            >
              {t(confirmButtonText || 'Cash out')}
            </LoaderButton>
          </div>
        )}
    </div>
  );
};

CashoutBase.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  icon: PropTypes.string,
  img: PropTypes.string,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  className: PropTypes.string,
  timerVariant: PropTypes.string,
  confirmButtonClassName: PropTypes.string,
  hasPartialCashout: PropTypes.bool,
  hasBack: PropTypes.bool,
  onBack: PropTypes.func,
  confirmButtonText: PropTypes.string,
  placeholder: PropTypes.string,
};

export default CashoutBase;
